import classNames from "classnames";
import { observer } from "mobx-react-lite";
import Image from "next/image";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { isAndroid, isDesktop } from "react-device-detect";
import { FiChevronDown, FiX } from "react-icons/fi";
import { IconFilter } from "../../../components/icons/icon-filter";
import { IconGrid } from "../../../components/icons/icon-grid";
import { IconList } from "../../../components/icons/icon-list";
import { IconLocation } from "../../../components/icons/icon-location";
import { IconSearch } from "../../../components/icons/icon-search";
import { LocationSource } from "./reseller-location-modal/context";
import { useStores } from "../../../stores";
import { SellerProfile } from "../../../stores/seller-store/seller-store.model";
import { trackEvents } from "../../../utils/analytics";
import {
  DOWNLOAD_APP_BANNER_CLOSED_KEY,
  PRODUCT_LAYOUT_KEY,
} from "../../../utils/constants";
import { storageLoad, storageSave } from "../../../utils/storage";
import { LocationPickerSkeleton } from "./loading-skeleton";
import { SellerPageRouter } from "../page-router.props";
import { ProductLayout } from "./product-list";
import { postMessageToBuyerApp } from "../../../utils/post-message";

interface Props {
  sellerData: SellerProfile;
  isReseller: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  setShowLocationPicker: (showLocationPicker: boolean) => void;
  setShowContactModal: (showContactModal: boolean) => void;
  setShowSortSettingsSheet: (showSortSettingsSheet: boolean) => void;
  productLayout: ProductLayout;
  setProductLayout: (layout: ProductLayout) => void;
  isStoreClosed: boolean;
}

export const SellerHeader = observer(
  ({
    sellerData,
    isReseller,
    searchQuery,
    setSearchQuery,
    setShowLocationPicker,
    setShowContactModal,
    setShowSortSettingsSheet,
    productLayout,
    setProductLayout,
    isStoreClosed,
  }: Props) => {
    const router: SellerPageRouter = useRouter();
    const { sellerStore, coverageAreaStore, cartStore } = useStores();

    const [isShowDownloadAppBanner, setShowDownloadAppBanner] =
      useState<boolean>(false);
    const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);

    const searchInputRef = useRef<HTMLInputElement>(null);

    const isSortFilterActive = useMemo(
      () => (router.query.order_by || router.query.sort_by ? true : false),
      [router.query]
    );

    useEffect(() => {
      async function init() {
        if (isReseller) {
          const isDownloadAppBannerClosed = await storageLoad(
            DOWNLOAD_APP_BANNER_CLOSED_KEY
          );
          if (isDownloadAppBannerClosed) setShowDownloadAppBanner(false);
          else if (!router.query.fromapp && (isAndroid || isDesktop))
            setShowDownloadAppBanner(true);
        }
        const savedProductLayout = await storageLoad(PRODUCT_LAYOUT_KEY);
        if (savedProductLayout)
          setProductLayout(savedProductLayout as ProductLayout);
      }
      init();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => {
      const { pathname, query } = router;
      if (!searchQuery && !query.q) return;
      if (searchQuery) query.q = searchQuery;
      else delete query.q;
      router.push({ pathname, query }, undefined, { shallow: true });
    };

    const handleKeyEnter = (evt: React.KeyboardEvent<HTMLInputElement>) => {
      if (evt.key === "Enter" || evt.code === "Enter") {
        handleSearch();
        if (searchInputRef?.current) searchInputRef.current.blur();
      }
    };

    const handleClearSearch = () => {
      const { pathname, query } = router;
      setSearchQuery("");
      delete query.q;
      router.push({ pathname, query }, undefined, { shallow: true });
    };

    const handleSwitchLayout = (mode: ProductLayout) => {
      setProductLayout(mode);
      storageSave(PRODUCT_LAYOUT_KEY, mode);
    };

    const handleCloseDownloadBanner = () => {
      setShowDownloadAppBanner(false);
      storageSave(DOWNLOAD_APP_BANNER_CLOSED_KEY, true);
    };

    const handleClickDownloadApp = () => {
      handleCloseDownloadBanner();
      window.open(process.env.NEXT_PUBLIC_URL_GPLAY_BUYER_APP, "_blank");
    };

    const handleOpenLocationPicker = () => {
      setShowLocationPicker(true);
      if (sellerData?.isReseller) {
        trackEvents({
          sellerUsername: sellerData?.User?.username,
          eventName: "produk_klik_pilih_alamat",
          eventCategory: "product",
          eventLabel: "change address",
          trackers: ["GTM", "AF", "ME"],
        });
      }
    };

    const onSortButtonClick = () => {
      if (!sellerData) return;
      if (!router?.query?.fromapp) {
        setShowSortSettingsSheet(true);
      } else {
        const { sort_by, order_by } = router.query;
        postMessageToBuyerApp(
          "product-list-filter",
          sort_by && order_by ? sort_by + "_" + order_by : "default"
        );
      }
    };

    const renderDownloadAppBanner = () => {
      return (
        isShowDownloadAppBanner && (
          <div className="px-5 py-3">
            <div className="bg-gray-100 p-2 rounded">
              <div className="flex items-center">
                <div id="header-download-app-banner-close-btnheader-download-app-banner-close-btn">
                  <FiX
                    size={16}
                    onClick={handleCloseDownloadBanner}
                    className="shrink-0 text-gray-300 cursor-pointer hover:text-gray-400"
                  />
                </div>
                <div className="mx-2 hidden mobile-m:block">
                  <Image
                    src="/img/jooalan/logo.png"
                    alt="CrediMart"
                    width={24}
                    height={24}
                  />
                </div>
                <div className="flex-1 flex items-center justify-between">
                  <div>
                    <div className="text-label-xs mobile-s:text-label-sm mobile-m:text-label-md font-bold">
                      Ingin Belanja Lebih Mudah?
                    </div>
                    <div className="text-label-xs">Download App Jooalan!</div>
                  </div>
                  <div className="shrink-0">
                    <div
                      onClick={handleClickDownloadApp}
                      className="bg-primary hover:bg-primary-700 cursor-pointer rounded-full px-2 py-1 text-white font-bold text-label-xs mobile-s:text-label-sm mobile-m:text-label-md"
                      id="product-download-app-banner-download-btn"
                      data-testid="product-download-app-banner-download-btn"
                    >
                      DOWNLOAD
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      );
    };

    const renderSearchBar = () => {
      return (
        <div className="flex items-center px-5 py-3 bg-transparent text-black">
          <div
            className="flex items-center flex-1 border border-fill-gray rounded-md p-2 mr-4 bg-white"
            id="product-search-input"
            data-testid="product-search-input"
          >
            <IconSearch width={20} height={20} className="text-gray-300" />
            <input
              ref={searchInputRef}
              className="outline-none text-label-sm px-2 w-full"
              value={searchQuery}
              placeholder="Cari Produk Disini..."
              onChange={(evt) => setSearchQuery(evt.target.value)}
              onKeyDown={handleKeyEnter}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => {
                setIsSearchFocused(false);
                handleSearch();
              }}
            />
            {!!searchQuery && (
              <FiX
                className={classNames(
                  "relative right-1 cursor-pointer transition-colors duration-150",
                  isSearchFocused ? "text-primary" : "text-secondary-gray"
                )}
                onClick={() => handleClearSearch()}
                size={18}
              />
            )}
          </div>
          <div className="flex items-center">
            <div
              className="relative"
              id="product-sort-open-btn"
              data-testid="product-sort-open-btn"
            >
              <IconFilter
                className={classNames({
                  "cursor-pointer": sellerData,
                  "cursor-not-allowed text-gray-300": !sellerData,
                })}
                width={20}
                height={20}
                onClick={onSortButtonClick}
              />
              {isSortFilterActive && (
                <div className="absolute top-[-2px] right-[-6px] ml-2 size-2 bg-primary rounded-full" />
              )}
            </div>
            <div className="border-l border-l-fill-gray w-[1px] h-6 mx-2"></div>
            {productLayout === ProductLayout.grid ? (
              <IconList
                className={classNames({
                  "cursor-pointer": sellerData,
                  "cursor-not-allowed text-gray-300": !sellerData,
                })}
                width={20}
                height={20}
                onClick={() => {
                  if (!sellerData) return;
                  handleSwitchLayout(ProductLayout.list);
                }}
              />
            ) : (
              <IconGrid
                className={classNames({
                  "cursor-pointer": sellerData,
                  "cursor-not-allowed text-gray-300": !sellerData,
                })}
                width={20}
                height={20}
                onClick={() => {
                  if (!sellerData) return;
                  handleSwitchLayout(ProductLayout.grid);
                }}
              />
            )}
          </div>
        </div>
      );
    };

    if (isStoreClosed) {
      return (
        <header>
          <div
            className="transition-all duration-100"
            data-section="headerInfo"
          >
            <div className="flex justify-between items-center px-5 py-3">
              <div className="w-full pr-2">
                <h1
                  className="text-lg sm:text-xl font-medium line-clamp-2"
                  title={sellerStore?.sellerProfile?.User?.name}
                >
                  {sellerStore?.sellerProfile?.User?.name}
                </h1>
                {sellerStore?.sellerProfile?.City?.name && (
                  <div className="flex items-center text-label-md mt-2">
                    <IconLocation
                      className="mr-1.5 text-primary"
                      width={16}
                      height={16}
                    />
                    {sellerStore.sellerProfile.City.name}
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowContactModal(true)}
                className="px-2 py-3 rounded-md shadow-light transition hover:bg-gray-50 font-bold text-primary"
                id="header-contact-btn"
                data-testid="header-contact-btn"
              >
                HUBUNGI
              </button>
            </div>
          </div>
        </header>
      );
    } else {
      return (
        <header>
          <div
            className="transition-all duration-100"
            data-section="headerInfo"
          >
            {renderDownloadAppBanner()}

            {/*
            <div className="flex justify-between items-center px-5 py-3">
              {!isReseller && sellerData && (
                <div className="w-full pr-2">
                  <h1
                    className="text-lg sm:text-xl font-medium line-clamp-2"
                    title={sellerStore?.sellerProfile?.User?.name}
                  >
                    {sellerStore?.sellerProfile?.User?.name}
                  </h1>
                  {sellerStore?.sellerProfile?.City?.name && (
                    <div className="flex items-center text-label-md mt-2">
                      <IconLocation
                        className="mr-1.5 text-primary"
                        width={16}
                        height={16}
                      />
                      {sellerStore.sellerProfile.City.name}
                    </div>
                  )}
                </div>
              )}
              {(isReseller || !sellerData) && (
                <div className="w-full flex flex-row items-center pr-2 text-label-sm">
                  <div className="mr-2">
                    <Image
                      src="/img/jooalan/logo.png"
                      alt="CrediMart"
                      width={24}
                      height={24}
                    />
                  </div>
                  {!coverageAreaStore?.selectedCoverageArea?.id &&
                  sellerData ? (
                    <LocationPickerSkeleton />
                  ) : (
                    sellerData && (
                      <div className="flex flex-col">
                        <span>Lokasi</span>
                        <div
                          onClick={handleOpenLocationPicker}
                          className="flex items-center cursor-pointer text-label-xs mobile-m:text-label-sm"
                          id="header-pick-location-open-btn"
                          data-testid="header-pick-location-open-btn"
                        >
                          {coverageAreaStore?.selectedCoverageArea
                            ?.locationSource === LocationSource.map ? (
                            <div className="max-w-[200px] line-clamp-1">
                              {cartStore?.savedCheckoutData?.customerAddress
                                ?.address || "-"}
                            </div>
                          ) : (
                            <div className="max-w-[200px] line-clamp-1">
                              {coverageAreaStore?.selectedCity?.name} -{" "}
                              {coverageAreaStore?.selectedDistrict?.name}
                            </div>
                          )}
                          <FiChevronDown className="mobile-m:ml-1 text-primary text-base shrink-0" />
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
              <button
                onClick={() => setShowContactModal(true)}
                className="px-2 py-3 rounded-md shadow-light transition hover:bg-gray-50 font-bold text-primary"
                id="header-contact-btn"
                data-testid="header-contact-btn"
              >
                HUBUNGI
              </button>
            </div>*/}
          </div>
          {renderSearchBar()}
        </header>
      );
    }
  }
);
